import * as React from 'react';
import { useCurrentCurrency, useCurrentLanguage } from '@avensia/excite';

const formatters: Record<string, Intl.NumberFormat> = {};

export function useFormatPriceParts() {
  const language = useCurrentLanguage();
  const defaultCurrency = useCurrentCurrency();

  return React.useCallback(
    (price: number, currency = defaultCurrency) => {
      const formatter = (formatters[`${currency}_${language}`] ??= new Intl.NumberFormat(language, {
        style: 'currency',
        currency,
      }));

      return formatter.formatToParts(price);
    },
    [defaultCurrency, language],
  );
}

const globalPriceFormatters = new Map<string, (price: number, language: string) => string>();

export function registerPriceFormatter(currency: string, formatter: (price: number, language: string) => string) {
  if (globalPriceFormatters.has(currency)) {
    console.warn(`Already registered a priceFormatter for currency ${currency}`);
  } else {
    globalPriceFormatters.set(currency, formatter);
  }
}

export function useFormatPrice() {
  const priceParts = useFormatPriceParts();
  const currentCurrency = useCurrentCurrency();
  const currentLanguage = useCurrentLanguage();

  return React.useCallback(
    (price: number, currency = currentCurrency, language = currentLanguage) => {
      const customFormatter = globalPriceFormatters.get(currency);
      if (customFormatter) {
        return customFormatter(price, language);
      }
      const parts = priceParts(price, currency);
      const isInteger = Number.isInteger(price);
      let string = '';

      for (const part of parts) {
        const isDecimalPart = part.type === 'decimal' || part.type === 'fraction';
        if (!(isDecimalPart && isInteger)) {
          string += part.value;
        }
      }

      return string;
    },
    [priceParts, currentCurrency, currentLanguage],
  );
}
